import _ from "lodash";
import translationKeys from "../../../translations/keys";
import { calculateTargetBitrate } from "./helpers";
import { TYPE_LINES, TYPE_POLYGONS } from "../../../hooks/useShapeDrawing/constants";
import { ANALYTICS_TYPES } from "../../../utils/cameras";

export const SETTINGS_TYPES = {
    select: 'select',
    toggle: 'toggle',
    schedule: 'schedule'
};


export const SETTINGS_SECTIONS = {
    CAMERA: 'camera',
    MOTION: 'motion',
    ALERTS: 'alerts',
    ANALYTICS: 'analytics',
    ALARM: 'alarm',
    TRIGGER: 'trigger_type',
};

const SCATI_PREFIXES = [
    "SEC-3", "SEM-3", "SED-3", "SEP-3", "SET-3",
    "SIC-3", "SIM-3", "SID-3",
    "SDLC-3", "SDLI-3", "SDLM-3", "SDL-3",
    "STC-3",
    "SL-FRT", "SL-5", "SL-6", "SL-7", "SL-9",
    "SEC-5", "SEM-5", "SED-5", "SEP-5", "SET-5",
    "SIC-5", "SIM-5", "SID-5",
    "SDLC-5", "SDLI-5", "SDLM-5", "SDL-5"
];

const isHikvisionOrScatiCamera = (camId) => {
    if (!camId) return false;
    if (camId.startsWith("DS-") || camId.startsWith("iDS-")) return true;
    return SCATI_PREFIXES.some(prefix => camId.startsWith(prefix));
};

export const SETTINGS_CATEGORIES = {
    camera: {
        id: SETTINGS_SECTIONS.CAMERA,
        text: translationKeys.cameras.CAMERA,
        icon: 'camera',
        options: {
            tags: {
                icon: 'tag',
                permissions: ['a', 'owner']
            },
            recording: {
                icon: 'recording',
                options: {
                    romEnabled: {
                        type: SETTINGS_TYPES.select,
                        id: 'romEnabled',
                        text: translationKeys.technical.recordingModes.RECORDING_MODE,
                        options: [
                            {
                                text: translationKeys.technical.recordingModes.ROM,
                                value: true
                            }, 
                            {
                                text: translationKeys.technical.recordingModes.CONTINUOUS,
                                value: false
                            } 
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.RECORDING_MODE,
                        permissions: ['a', 'owner'],
                        typeof: 'boolean'
                    }, 
                    recordingResolution: {
                        type: SETTINGS_TYPES.select,
                        id: 'recordingResolution',
                        text: translationKeys.technical.resolutions.RECORDED_VIDEO_QUALITY,
                        options: [
                            {
                                text: translationKeys.technical.resolutions['SD'],
                                value: 'SD'
                            },
                            {
                                text: translationKeys.technical.resolutions['2MP'],
                                value: '2MP'
                            }, 
                            {
                                text: translationKeys.technical.resolutions['4MP'],
                                value: '4MP'
                            }, 
                            {
                                text: translationKeys.technical.resolutions['8MP'],
                                value: '8MP'
                            }
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.RECORDED_VIDEO_QUALITY,
                        permissions: ['a', 'owner'],
                        changeWarning: {
                            condition: (newValue, camerasConfigs) => newValue === 'SD' && camerasConfigs.filter(c => c.analytics_enabled).length > 0,
                            updates: { analytics_enabled: false, camera_settings: { analytics_alerts: 0 } },
                            text: translationKeys.technical.settings.cameraSettings.warnings.OPTIMISE_ANALYTICS,
                            warning: "optimise_analytics"
                        }
                    }, 
                    bitRate: {
                        type: SETTINGS_TYPES.select,
                        id: 'bitrate_multiplier',
                        options: _.range(1, 20, 0.1).map(n => ({ text: Number(n.toFixed(1)), value: Number(n.toFixed(1))})),
                        text: translationKeys.technical.settings.cameraSettings.bit_rate.BIT_RATE,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.BIT_RATE,
                        info: (cameras) => cameras.length > 1 ? "" : calculateTargetBitrate(cameras[0]),
                        permissions: ['s'] // staff only
                    },
                    videoCodec: {
                        type: SETTINGS_TYPES.select,
                        id: 'vcodec',
                        text: translationKeys.technical.codec.RECORDING_CODEC,
                        options: [
                            {
                                text: translationKeys.technical.codec.H264,
                                value: 'h264'
                            },
                            {
                                text: translationKeys.technical.codec.H265,
                                value: 'h265'
                            }
                        ],
                        getValue: (settings) => _.isNil(settings.vcodec) ? 'h264' : settings.vcodec,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.RECORDING_CODEC,
                        permissions: ['a', 'owner']
                    },
                    aspectRatio: {
                        type: SETTINGS_TYPES.select,
                        id: 'aspect_ratio',
                        text: translationKeys.technical.aspect_ratio.ASPECT_RATIO,
                        options: [
                            {
                                text: translationKeys.technical.aspect_ratio["169"],
                                value: '16:9'
                            },
                            {
                                text: translationKeys.technical.aspect_ratio["43"],
                                value: '4:3'
                            }
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.ASPECT_RATIO,
                        permissions: ['s'],
                        staffWarning: "Depends on adapter build, may not work as expected."
                    },
                    cbr: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'cbr',
                        text: translationKeys.technical.cbr.CBR,
                        permissions: ['s'],
                        staffWarning: "Depends on adapter build, may not work as expected."
                    },
                    min_framerate: {
                        type: SETTINGS_TYPES.select,
                        id: 'min_framerate',
                        text: translationKeys.technical.framerate.MIN,
                        options: [{ text: 'unset', value: '' }].concat(_.range(1, 100).map(n => ({ text: n, value: n}))),
                        permissions: ['s'],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.MIN_FRAMERATE,
                        getValue: ({ min_framerate }) => _.isNil(min_framerate) ? '' : min_framerate,
                        staffWarning: "Depends on adapter build, may not work as expected."
                    },
                    max_framerate: {
                        type: SETTINGS_TYPES.select,
                        id: 'max_framerate',
                        text: translationKeys.technical.framerate.MAX,
                        options: [{ text: 'unset', value: '' }].concat(_.range(1, 100).map(n => ({ text: n, value: n}))),
                        permissions: ['s'],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.MAX_FRAMERATE,
                        getValue: ({ max_framerate }) => _.isNil(max_framerate) ? '' : max_framerate,
                        staffWarning: "Depends on adapter build, may not work as expected."
                    },
                },
                permissions: ['a', 'owner']
            },
            live: {
                icon: 'connection',
                options: {
                    mainstreamLive: {
                        type: SETTINGS_TYPES.select,
                        id: 'mainstream_live',
                        text: translationKeys.technical.resolutions.LIVE_VIDEO_QUALITY,
                        options: [
                            {
                                text: translationKeys.technical.resolutions.SD,
                                value: 0
                            },
                            {
                                text: translationKeys.technical.resolutions.HD,
                                value: 1
                            } ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.LIVE_VIDEO_QUALITY,
                        permissions: ['a', 'owner']
                    },
                },
                permissions: ['a', 'owner']
            },
            controls: {
                icon: 'arrows-move',
                options: {
                    ptzEnabled: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'ptz_enabled',
                        text: translationKeys.cameras.ptz.PTZ,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.PTZ,
                        getValue: (camera) => _.isNil(camera.ptz_enabled) ? false : camera.ptz_enabled,
                        permissions: ['a', 'owner']
                    }
                },
                permissions: ['a', 'owner']
            },
            stills: {
                icon: 'image',
                options: {
                    stillsEnabled: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'stills_enabled',
                        text: translationKeys.technical.settings.cameraSettings.STILLS,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.STILLS,
                        permissions: ['a', 'owner']
                    },
                },
                permissions: ['a', 'owner']
            },
            sound: {
                icon: 'audio',
                options: {
                    audioEnabled: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'audio_enabled',
                        text: translationKeys.technical.settings.cameraSettings.AUDIO,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.AUDIO,
                        getValue: (camera) => _.isNil(camera.audio_enabled) ? false : camera.audio_enabled,
                        permissions: ['a', 'owner']
                    },
                    talkbackEnabled: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'talkback_enabled',
                        text: translationKeys.technical.settings.cameraSettings.TALKBACK,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.TALKBACK,
                        getValue: (camera) => _.isNil(camera.talkback_enabled) ? false : camera.talkback_enabled,
                        permissions: ['a', 'owner']
                    }
                },
                permissions: ['a', 'owner']
            },
            schedule: {
                icon: 'schedule',
                options: {
                    schedule: {
                        type: SETTINGS_TYPES.schedule,
                        id: 'recording_schedule',
                        text: translationKeys.technical.settings.cameraSettings.CAMERA_SCHEDULE,
                        options: [
                            {
                                value: 1,
                                text: translationKeys.technical.settings.cameraSettings.schedules.r["1"]
                            },
                            {
                                value: 0,
                                text: translationKeys.technical.settings.cameraSettings.schedules.r["0"]
                            }
                        ],
                        scheduleControlKey: 'r',
                        description: translationKeys.technical.settings.cameraSettings.descriptions.SCHEDULE,
                        permissions: ['a', 'owner']
                    }
                },
                permissions: ['a', 'owner']
            }
        },
        permissions: ['a', 'owner']
    },
    motion: {
        id: SETTINGS_SECTIONS.MOTION,
        text: translationKeys.events.MOTION,
        icon: 'motion',
        options: {
            section: {
                icon: 'motion',
                options: {
                    motionSensitivity: {
                        type: SETTINGS_TYPES.select,
                        id: 'motion_trigger_level',
                        text: translationKeys.technical.settings.cameraSettings.MOTION_SENSITIVITY,
                        options: 
                            [1,2,3,4,5,6,7,8,9,10].map(no => ({
                                text: translationKeys.technical.settings.cameraSettings.motionSensitivities[no],
                                value: no
                            })),
                        description: translationKeys.technical.settings.cameraSettings.descriptions.MOTION_SENSITIVITY,
                        permissions: ['a', 'owner']
                    },
                    motionGrid: {
                        type: 'matrix',
                        id: 'motion_grid',
                        text: translationKeys.technical.settings.cameraSettings.DETECTION_ZONES,
                        hideForMultiple: true,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.DETECTION_ZONES,
                        permissions: ['a', 'owner']
                    }
                },
                permissions: ['a', 'owner']
            }
        },
        permissions: ['a', 'owner']
    },
    analytics: {
        id: SETTINGS_SECTIONS.ANALYTICS,
        text: translationKeys.technical.analytics.ANALYTICS,
        icon: 'analytics',
        title: translationKeys.technical.analytics.ANALYTICS_FEATURES,
        conditionalDescriptions: [
            {
                condition: (cameras) => _.some(_.values(cameras), ({ recordingResolution }) => recordingResolution === 'SD'),
                message: translationKeys.technical.analytics.addons.DISCLAIMER_SD
            }
        ],
        options: {
            analytics_enabled: {
                icon: 'analytics',
                options: {
                    analyticsEnabled: {
                        // deprecated addon so hide if it's not enabled
                        hide: (cameras) => _.some(_.values(cameras), ({ analytics_enabled, analytics_scheme }) => analytics_enabled !== 1 || (+analytics_enabled === 1 && !analytics_scheme?.some(s => s === 'videoloft'))),
                        type: SETTINGS_TYPES.toggle,
                        text: translationKeys.technical.analytics.STANDARD_ANALYTICS,
                        description: translationKeys.technical.analytics.STANDARD_ANALYTICS_DESCRIPTION,
                        id: 'analytics_enabled',
                        permissions: ['a', 'owner'],
                        disabled: (cameras) => _.some(_.values(cameras), ({ recordingResolution }) => recordingResolution === 'SD'),
                        getValue: (camera) => +camera.analytics_enabled > 0 && (_.isNil(camera.analytics_scheme) || camera?.analytics_scheme.includes('videoloft'))
                    },
                    videoloftexp: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'smart',
                        text: translationKeys.technical.analytics.addons['smart-motion'],
                        description: translationKeys.technical.analytics.addons.descriptions['smart-motion'],
                        learnMoreLink: 'https://videoloft.com/smart-motion/',
                        permissions: ['a', 'owner'],
                        disabled: (cameras) => _.some(_.values(cameras), ({ recordingResolution }) => recordingResolution === 'SD'),
                        getValue: (camera) => +camera.analytics_enabled > 0 && !_.isNil(camera.analytics_scheme) && (camera?.analytics_scheme?.includes(ANALYTICS_TYPES.OBJECTS) || camera?.analytics_scheme?.includes(ANALYTICS_TYPES.SMART)),
                        addon: true
                    }
                },
                permissions: ['a', 'owner']
            },
            lpr: {
                icon: 'car',
                preWarning: translationKeys.technical.analytics.MOBILE_CONFIGURE_ZONES,
                options: {
                    lpr: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'lpr',
                        text: translationKeys.technical.analytics.addons.lpr,
                        description: translationKeys.technical.analytics.addons.descriptions.lpr,
                        learnMoreLink: 'https://videoloft.com/license-plate-recognition/',
                        permissions: ['a', 'owner'],
                        disabled: (cameras) => _.some(_.values(cameras), ({ recordingResolution }) => recordingResolution === 'SD'),
                        getValue: (camera) => +camera.analytics_enabled > 0 && !_.isNil(camera.analytics_scheme) && (camera?.analytics_scheme?.includes('lpr') || camera?.analytics_scheme?.includes('lpr-night')),
                        zoneConfig: {
                            type: TYPE_POLYGONS,
                            buttonText: translationKeys.vehicles.detectionZones.MANAGE_ZONES,
                            disabledForMultiple: true,
                            variant: 'lpr'
                        },
                        changeWarning: {
                            condition: (newValue) => newValue, // popup if turning it on
                            text: translationKeys.technical.analytics.addons.warnings.lpr,
                            warning: "enable_lpr",
                            heading: translationKeys.technical.settings.cameraSettings.warnings.ENABLE_LPR
                        },
                        addon: true
                    }
                },
                permissions: ['a', 'owner'],
            },
            people: {
                icon: 'people',
                options: {
                    people: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'people-lc',
                        text: translationKeys.technical.analytics.addons.people,
                        description: translationKeys.technical.analytics.addons.descriptions.people,
                        learnMoreLink: 'https://videoloft.com/people-counting/',
                        permissions: ['a', 'owner'],
                        disabledForMultiple: true,
                        disabled: (cameras) => _.some(_.values(cameras), ({ recordingResolution }) => recordingResolution === 'SD'),
                        getValue: (camera) => +camera.analytics_enabled > 0 && !_.isNil(camera.analytics_scheme) && (camera?.analytics_scheme?.includes('people') || camera?.analytics_scheme?.includes('people-lc')),
                        zoneConfig: {
                            type: TYPE_LINES,
                            buttonText: translationKeys.peopleCounting.lineCrossings.MANAGE_COUNT_LINES,
                            disabledForMultiple: true,
                            variant: 'peopleCounting' // used for AnalyticsConfigModal
                        },
                        addon: true
                    },
                },
                permissions: ['a', 'owner'],
            },
            ppe: {
                icon: 'hardhat',
                options: {
                    ppe: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'ppe',
                        text: translationKeys.technical.analytics.addons.ppe,
                        description: translationKeys.technical.analytics.addons.descriptions.ppe,
                        learnMoreLink: 'https://videoloft.com/ppe-detection/',
                        permissions: ['a', 'owner'],
                        disabledForMultiple: true,
                        disabled: (cameras) => _.some(_.values(cameras), ({ recordingResolution }) => recordingResolution === 'SD'),
                        getValue: (camera) => +camera.analytics_enabled > 0 && !_.isNil(camera.analytics_scheme) && camera?.analytics_scheme?.includes('ppe'),
                        zoneConfig: {
                            type: TYPE_LINES,
                            buttonText: translationKeys.ppe.detectionLines.MANAGE_LINES,
                            disabledForMultiple: true,
                            variant: 'ppe' // used for AnalyticsConfigModal
                        },
                        addon: true
                    },
                },
                permissions: ['a', 'owner'],
            },
            orders: {
                icon: 'kitchen',
                options: {
                    orders: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'orders',
                        text: translationKeys.technical.analytics.addons.orders,
                        permissions: ['s'],
                        getValue: (camera) => +camera.analytics_enabled > 0 && !_.isNil(camera.analytics_scheme) && camera?.analytics_scheme?.includes('orders')
                    },
                },
                permissions: ['s'],
            }
        },
        permissions: ['a', 'owner']
    },
    alerts: {
        id: SETTINGS_SECTIONS.ALERTS,
        text: translationKeys.technical.settings.cameraSettings.ALERTS,
        icon: 'bell',
        options: {
            status: {
                icon: 'thermometer',
                options: {
                    offlineEmails: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'offline_email',
                        text: translationKeys.technical.settings.cameraSettings.OFFLINE_EMAILS,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.OFFLINE_EMAILS,
                        permissions: ['a', 'owner'],
                        getValue: (settings) => _.isNil(settings.offline_email) ? false : Boolean(settings.offline_email)
                    },
                    offlinePush: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'offline_push',
                        text: translationKeys.technical.settings.cameraSettings.OFFLINE_PUSH,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.OFFLINE_PUSH,
                        permissions: ['a', 'owner'],
                        getValue: (settings) => _.isNil(settings.offline_push) ? false : Boolean(settings.offline_push)
                    },
                },
                permissions: ['a', 'owner']
            },
            email: {
                icon: 'bell',
                options: {
                    alerts: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'send_alert_state',
                        text: translationKeys.technical.settings.cameraSettings.DETECTION_ALERTS,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.ALERTS,
                        scheduleControlKey: 'a',
                        permissions: ['a', 'r', 'owner'],
                        getValue: (settings) => _.isNil(settings.send_alert_state) ? false : Boolean(settings.send_alert_state)
                    },
                    emailAlerts: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'send_alert_email',
                        text: translationKeys.technical.settings.cameraSettings.EMAIL_ALERTS,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.EMAIL_ALERTS,
                        permissions: ['a', 'r', 'owner'],
                        getValue: (settings) => _.isNil(settings.send_alert_email) ? false : Boolean(settings.send_alert_email)
                    },
                    pushAlerts: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'send_alert_push',
                        text:  translationKeys.technical.settings.cameraSettings.PUSH_ALERTS,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.PUSH_ALERTS,
                        permissions: ['a', 'r', 'owner'],
                        getValue: (settings) => _.isNil(settings.send_alert_push) ? false : Boolean(settings.send_alert_push)
                    },
                    minGapEmails: {
                        type: SETTINGS_TYPES.select,
                        id: 'min_gap_send_alert_email',
                        text: translationKeys.technical.settings.cameraSettings.MIN_GAP_EMAILS,
                        options: [
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 10 }],
                                value: 600
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 30 }],
                                value: 1800
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 1 }],
                                value: 3600
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 3 }],
                                value: 10800
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 12 }],
                                value: 43200
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 24 }],
                                value: 86400
                            }
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.MIN_GAP_EMAILS,
                        permissions: ['a', 'r', 'owner']
                    },
                    minGapPush: {
                        type: SETTINGS_TYPES.select,
                        id: 'min_gap_alert_push',
                        text: translationKeys.technical.settings.cameraSettings.MIN_GAP_PUSH,
                        options: [
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 0 }],
                                value: 1
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 1 }],
                                value: 60
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 5 }],
                                value: 300
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 15 }],
                                value: 900
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 30 }],
                                value: 1800
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 1 }],
                                value: 3600
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 2 }],
                                value: 7200
                            }
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.MIN_GAP_PUSH,
                        permissions: ['a', 'r', 'owner']
                    },
                    analyticsAlerts: {
                        type: SETTINGS_TYPES.select,
                        id: 'analytics_alerts',
                        text: translationKeys.technical.settings.cameraSettings.ALERT_TRIGGER,
                        options: [
                            {
                                text: translationKeys.events.MOTION,
                                value: 0
                            },
                            {
                                text: translationKeys.events.analytics.ANALYTICS,
                                value: 1
                            }
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.ALERT_TRIGGER,
                        // hide this if some of the cameras selected have analytics disabled or their scheme is different from videoloft/null
                        hide: (cameras) => _.some(_.values(cameras), ({ analytics_enabled, analytics_scheme }) => !analytics_enabled || (!_.isNil(analytics_scheme) && !analytics_scheme?.includes('videoloft'))),
                        permissions: ['owner'],
                        warnings: [
                            {
                                // show "no rules setup" warning if analytics is the trigger and there are no rules setup
                                condition: (cameras) => {
                                    return _.every(_.values(cameras), ({ analytics_alerts, alertRules }) => parseInt(analytics_alerts) === 1 && (!alertRules || alertRules.length === 0))
                                },
                                text: translationKeys.technical.settings.cameraSettings.warnings.NO_ALERT_RULES
                            },
                            {
                                // show diff message warning if analytics is the trigger and there are rules setup on some cameras but not all
                                //TODO: change this message
                                condition: (cameras) => _.every(_.values(cameras), ({ analytics_alerts }) => parseInt(analytics_alerts) === 1) && _.some(_.values(cameras), ({ analytics_alerts, alertRules }) => (!alertRules || alertRules.length === 0) && analytics_alerts) && !_.every(_.values(cameras), ({ analytics_alerts, alertRules }) => parseInt(analytics_alerts) === 1 && (!alertRules || alertRules.length === 0)),
                                text: translationKeys.technical.settings.cameraSettings.warnings.SOME_ALERT_RULES
                            }
                        ]
                    },
                    analyticsRules: {
                        type: 'rules',
                        id: 'alertRules',
                        text: translationKeys.technical.settings.cameraSettings.ANALYTICS_ALERT_RULES,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.ALERT_RULES,
                        hide: (cameras) => _.some(_.values(cameras), ({ analytics_alerts }) => parseInt(analytics_alerts) !== 1),
                        permissions: ['owner']
                    }
                },
                permissions: ['a', 'r', 'owner']
            },
            schedule: {
                icon: 'schedule',
                options: {
                    schedule: {
                        type: SETTINGS_TYPES.schedule,
                        id: 'alerts_schedule',
                        text: translationKeys.technical.settings.cameraSettings.ALERTS_SCHEDULE,
                        options: [
                            {
                                value: 1,
                                text: translationKeys.technical.settings.cameraSettings.schedules.a["1"]
                            },
                            {
                                value: 0,
                                text: translationKeys.technical.settings.cameraSettings.schedules.a["0"]
                            }
                        ],
                        scheduleControlKey: 'a',
                        description: translationKeys.technical.settings.cameraSettings.descriptions.SCHEDULE,
                        permissions: ['a', 'r', 'owner']
                    }
                },
                permissions: ['a', 'r', 'owner']
            }
        },
        permissions: ['a', 'r', 'owner']
    },
    alarm: {
        id: SETTINGS_SECTIONS.ALARM,
        text: translationKeys.technical.settings.cameraSettings.ALARM,
        icon: 'warning',
        hide: (cameras) => _.values(cameras).map(c => c.camId).some(id => !(id.startsWith("DS-") || id.startsWith("iDS-"))),
        options: {
            alarm_on: {
                icon: 'bell',
                options: {
                    alarmOutput: {
                        type: SETTINGS_TYPES.toggle,
                        id: 'alarm_state',
                        text: translationKeys.technical.settings.cameraSettings.ALARM_STATE,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.ALARM_STATE,
                        scheduleControlKey: 'alarm_state',
                        permissions: ['a', 'owner']
                    },
                    minGapAlarm: {
                        type: SETTINGS_TYPES.select,
                        id: 'min_gap_alarm',
                        text: translationKeys.technical.settings.cameraSettings.MIN_GAP_ALARM,
                        options: [
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 1 }],
                                value: 60
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 5 }],
                                value: 300
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 15 }],
                                value: 900
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 30 }],
                                value: 1800
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 1 }],
                                value: 3600
                            },
                            {
                                text: [translationKeys.time.X_HOURS, { count: 2 }],
                                value: 7200
                            }                            
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.MIN_GAP_ALARM,
                        permissions: ['a', 'owner']
                    },
                    alarmOutputLength: {
                        type: SETTINGS_TYPES.select,
                        id: 'alarm_length',
                        text: translationKeys.technical.settings.cameraSettings.ALARM_LENGTH,
                        options: [
                            {
                                text: [translationKeys.time.X_SECONDS, { count: 3 }],
                                value: 3
                            },
                            {
                                text: [translationKeys.time.X_SECONDS, { count: 5 }],
                                value: 5
                            },
                            {
                                text: [translationKeys.time.X_SECONDS, { count: 30 }],
                                value: 30
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 1 }],
                                value: 60
                            },
                            {
                                text: [translationKeys.time.X_MINUTES, { count: 4 }],
                                value: 240
                            }
                        ],
                        description: translationKeys.technical.settings.cameraSettings.descriptions.ALARM_LENGTH,
                        permissions: ['a', 'owner']
                    }
                },
                permissions: ['a', 'owner']
            },
            schedule: {
                icon: 'schedule',
                options: {
                    schedule: {
                        type: SETTINGS_TYPES.schedule,
                        id: 'alarm_schedule',
                        text: translationKeys.technical.settings.cameraSettings.ALARM_SCHEDULE,
                        options: [
                            {
                                value: 1,
                                text: translationKeys.technical.settings.cameraSettings.schedules.alarm_state["1"]
                            },
                            {
                                value: 0,
                                text: translationKeys.technical.settings.cameraSettings.schedules.alarm_state["0"]
                            }
                        ],
                        scheduleControlKey: 'alarm_state',
                        description: translationKeys.technical.settings.cameraSettings.descriptions.SCHEDULE,
                        permissions: ['a', 'owner'],
                        otherCamerasFilter: ({ camId }) => camId?.startsWith("DS-") || camId?.startsWith("iDS-")
                    }
                },
                permissions: ['a', 'owner']
            }
        },
        permissions: ['a', 'owner']
    },
    trigger_type: {
        id: SETTINGS_SECTIONS.TRIGGER,
        text: translationKeys.technical.settings.cameraSettings.TRIGGER,
        icon: 'trigger',
        hide: (cameras) => _.values(cameras).map(c => c.camId).some(id => !isHikvisionOrScatiCamera(id)),
        options: {
            trigger_type: {
                icon: 'trigger',
                options: {
                    trigger_type: {
                        type: SETTINGS_TYPES.select,
                        id: 'trigger_type',
                        options: [
                            {
                                text: translationKeys.technical.settings.cameraSettings.triggerTypes.APP,
                                value: 0
                            },
                            {
                                text: translationKeys.technical.settings.cameraSettings.triggerTypes.HIK,
                                value: 1
                            },
                            {
                                text: translationKeys.technical.settings.cameraSettings.triggerTypes.COMBINED,
                                value: 2
                            }
                        ],
                        scheduleControlKey: 'trigger_type',
                        text: translationKeys.technical.settings.cameraSettings.TRIGGER_TYPE,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.TRIGGER_TYPE,
                        permissions: ['a', 'owner'],
                    },
                    schedule: {
                        type: SETTINGS_TYPES.schedule,
                        id: 'trigger_type_schedule',
                        text: translationKeys.technical.settings.cameraSettings.TRIGGER_TYPE_SCHEDULE,
                        description: translationKeys.technical.settings.cameraSettings.descriptions.SCHEDULE,
                        permissions: ['a', 'owner'],
                        scheduleControlKey: 'trigger_type',
                        options: [
                            {
                                value: 0,
                                text: translationKeys.technical.settings.cameraSettings.triggerTypes.APP
                            },
                            {
                                value: 1,
                                text: translationKeys.technical.settings.cameraSettings.triggerTypes.HIK
                            },
                            {
                                value: 2,
                                text: translationKeys.technical.settings.cameraSettings.triggerTypes.COMBINED
                            }
                        ],
                        otherCamerasFilter: ({ camId }) => isHikvisionOrScatiCamera(camId)
                    },
                },
                permissions: ['a', 'owner']
            }
        },
        permissions: ['a', 'owner']
    }
};

export const MAIN_CAMERA_TOGGLES = {
    cameraOn: {
        text: translationKeys.technical.settings.cameraSettings.CAMERA_ON,
        id: 'camera_on',
        hideIfOffline: true,
        description: translationKeys.technical.settings.cameraSettings.descriptions.CAMERA_ON,
        scheduleControlKey: 'r',
        permissions: ['a', 'owner']
    },
    cloudRecordingEnabled: {
        text: translationKeys.technical.cloudRecording.CLOUD_RECORDING,
        id: 'cloudRecordingEnabled',
        description: translationKeys.technical.settings.cameraSettings.descriptions.CLOUD_RECORDING,
        permissions: ['owner']
    },
    hdmi_enabled: {
        text: translationKeys.technical.settings.cameraSettings.HDMI,
        id: 'hdmi_enabled',
        hideIfOffline: true,
        getValue: (camera) => Boolean(camera.hdmi_enabled),
        description: translationKeys.technical.settings.cameraSettings.descriptions.HDMI,
        permissions: ['a', 'owner'],
        whiteLabelCondition: ({ isScati }) => !isScati
    }
};

export const VISIBLE_MAIN_CAMERA_TOGGLES = _.values(MAIN_CAMERA_TOGGLES);
export const SETTINGS_CATEGORIES_ARRAY = _.values(SETTINGS_CATEGORIES);

export const SETTING_IDS_ARRAY = [
    // IDs from MAIN_CAMERA_TOGGLES
    ..._.map(
      _.filter(MAIN_CAMERA_TOGGLES, 'id'),
      'id'
    ),
    // IDs from SETTINGS_CATEGORIES
    ..._.flatMap(SETTINGS_CATEGORIES_ARRAY, category => 
      _.flatMap(_.values(category.options), option => 
        _.map(option.options, 'id')
      )
    )
  ];

export const USER_SETTINGS = ['send_alert_state','send_alert_email', 'send_alert_push', 'min_gap_send_alert_email', 'min_gap_alert_push'];

